import {
  Box,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Applicant, QibDetailsType } from '../../redux-store/types/api-types';
import { styles } from '../../Styles/styles';
import React, { useEffect } from 'react';
import {
  QibQuestionType,
  QibValues,
  _initialQibValues,
  _singleQibQuestionType,
  shouldShowQuestion,
  shouldShowRemarks,
  DUE_DILIGENCE_VALIDATION_DATE,
} from '../investors/qibChecklist';
import { YES_NO_MASTER } from '../../utils/constant';
import { checkValidationBasedOnDate } from '../../utils/utilityFunctions';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from '../../redux-store/reducers';
import { QibQue } from '../../redux-store/actions';

const QibForm: React.FC<{ values: QibValues }> = ({ values }) => {
  const showQ = (q: _singleQibQuestionType) => shouldShowQuestion(q, values);

  const showR = (q: _singleQibQuestionType) => shouldShowRemarks(q, values);

  return (
    <Box>
      {values.qibQue.map((section: QibQuestionType, i: number) => (
        <Box key={i} sx={{ mb: 4 }}>
          {/* Section Title */}
          <Typography variant="h6" sx={{ mb: 2 }}>
            {section.section}
          </Typography>

          <Divider sx={{ mb: 3 }} />

          <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>Question</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '35%' }}>Response</TableCell>
                  <TableCell sx={{ fontWeight: 'bold', width: '30%' }}>
                    {section.remarkLabel ?? 'Remarks'}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {section.questions.map((q) => {
                  if (!showQ(q)) return null;

                  return (
                    <TableRow key={q.id}>
                      {/* Question Label */}
                      <TableCell sx={{ verticalAlign: 'top' }}>{q.label}</TableCell>

                      {/* Response */}
                      <TableCell>
                        {/* YES/NO */}
                        {q.type === 'yesno' && (YES_NO_MASTER[values.qib?.[q.id] ?? ''] || '--')}

                        {/* MULTI */}
                        {/* TEXT */}
                        {((q.type === 'multi' && q.options) || q.type === 'text') &&
                          (values.qib?.[q.id] ? values.qib?.[q.id] : '--')}
                      </TableCell>

                      {/* Remarks */}
                      <TableCell>
                        {showR(q)
                          ? values.qib?.[`${q.id}Remarks`]
                            ? values.qib?.[`${q.id}Remarks`]
                            : '--'
                          : '--'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </Box>
  );
};

export default function QibCheckList({
  applicants,
}: {
  applicants: Partial<Applicant>[];
}): JSX.Element {
  const [initialQibValues, setInitialQibValues] = React.useState<QibValues>({
    qib: _initialQibValues,
    qibQue: [] as unknown as QibQuestionType[],
    saveType: 'save for later',
  });
  const dispatch = useDispatch();
  const { application } = useSelector((store: RootStateType) => store.application);
  const [qibJson, setQibJson] = React.useState<QibQuestionType[]>([]);
  useEffect(() => {
    // Fetch existing data from API and set as initial values
    async function fetchQibData() {
      try {
        // Simulate API call
        const res = ((await dispatch(QibQue())) || []) as unknown as QibQuestionType[];
        setQibJson(res);
      } catch (error) {
        console.error('Error fetching QIB data:', error);
      }
    }

    fetchQibData();
  }, []);
  useEffect(() => {
    const hasSavedQibDetail = Object.keys(application?.qibDetail || {}).length > 0;
    setInitialQibValues((prev) => ({
      ...prev,
      qib: hasSavedQibDetail
        ? ({
            ...prev.qib,
            ...application?.qibDetail,
          } as QibDetailsType)
        : prev.qib,
      qibQue: qibJson.filter(
        (section) =>
          section.applicationType === application?.applicationType || !section.applicationType
      ),
    }));
  }, [application, qibJson]);
  return (
    <>
      <Box sx={styles.ApplicationDetailsCommonLayout} mt={5}>
        <Box sx={{ p: 2 }}>
          <Grid container sx={{ mt: 1, pl: { xs: 2, sm: 10 }, pr: { xs: 2, sm: 10 } }}>
            <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
              <Typography
                component="h2"
                sx={{ fontSize: 18, fontWeight: 600, color: 'primary.main', m: 0 }}>
                Qib CheckList
              </Typography>
            </Grid>
            <QibForm values={initialQibValues} />

            {checkValidationBasedOnDate(
              application?.createdAt || '',
              DUE_DILIGENCE_VALIDATION_DATE
            ) &&
              initialQibValues.qib?.isBeneficialOwner === 'yes' && (
                <Grid item xs={12} sx={{ width: '100%', mt: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Beneficial Owner Details
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  {(application?.beneficialowners || []).filter((bo) => bo.isActive !== false)
                    .length ? (
                    <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Beneficiary Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                              Tax Payer Identification Type
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                              Tax Payer Identification Number
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(application?.beneficialowners || [])
                            .filter((bo) => bo.isActive !== false)
                            .map((bo, index) => (
                              <TableRow key={bo.id ?? index}>
                                <TableCell>{bo.name || '--'}</TableCell>
                                <TableCell>{bo.taxPayerIdType || '--'}</TableCell>
                                <TableCell>{bo.taxPayerIdNumber || '--'}</TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography sx={{ color: 'text.secondary' }}>--</Typography>
                  )}
                </Grid>
              )}

            {checkValidationBasedOnDate(
              application?.createdAt || '',
              DUE_DILIGENCE_VALIDATION_DATE
            ) &&
              initialQibValues.qib?.isPartOfSameGroup === 'yes' && (
                <Grid item xs={12} sx={{ width: '100%', mt: 2 }}>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Related Party Details
                  </Typography>
                  <Divider sx={{ mb: 3 }} />
                  {(application?.relatedparties || []).filter((rp) => rp.isActive !== false)
                    .length ? (
                    <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell sx={{ fontWeight: 'bold' }}>Related Party Name</TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                              Tax Payer Identification Type
                            </TableCell>
                            <TableCell sx={{ fontWeight: 'bold' }}>
                              Tax Payer Identification Number
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(application?.relatedparties || [])
                            .filter((rp) => rp.isActive !== false)
                            .map((rp, index) => (
                              <TableRow key={rp.id ?? index}>
                                <TableCell>{rp.name || '--'}</TableCell>
                                <TableCell>{rp.taxPayerIdType || '--'}</TableCell>
                                <TableCell>{rp.taxPayerIdNumber || '--'}</TableCell>
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <Typography sx={{ color: 'text.secondary' }}>--</Typography>
                  )}
                </Grid>
              )}
          </Grid>
        </Box>
      </Box>
    </>
  );
}
